import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from 'react'
import { apiClient } from '@/services/apiClient'

// Types
interface Deal {
  id: string
  pipelineKanbanColumnId: string
  ownerUserId: string
  title: string
  geoCenter: string | null
  createTimestamp: string
  updateTimestamp: string
  archiveTimestamp: string | null
  deleteTimestamp: string | null
  isArchived: boolean
  informations: Record<string, any>
  hash: string | null
  shareInformations: any
  shareInformationsExpiredTimestamp: string
  status?: 'open' | 'won' | 'lost'
  firstName?: string
  lastName?: string
  contact?: string
}

interface DealLand extends Deal {
  id: string
  pipelineKanbanDealId: string
  landId: string
  createTimestamp: string
  updateTimestamp: string
  deleteTimestamp: string | null
  isDeleted: boolean
}

interface CreateDealPayload {
  title: string
  columnId: string
  informations?: Record<string, any>
  status?: 'open' | 'won' | 'lost'
  participants?: Array<{ id: string; name: string; role?: string }>
}

interface UpdateDealPayload {
  title?: string
  informations?: Record<string, any>
}

interface DealParticipant {
  id: string
  pipelineKanbanDealId: string
  userId: string
  createTimestamp: string
  updateTimestamp: string
  deleteTimestamp: string | null
  user: {
    id: string
    firstName: string
    lastName: string
    email: string
    photo: string
  }
}

interface DealsContextState {
  landId: string | null
  landData: any | null
  deals: DealLand[]
  loading: boolean
  error: string | null
  total: number

  // Methods
  fetchDeals: (landId: string) => Promise<void>
  addDeal: (dealData: CreateDealPayload) => Promise<void>
  updateDeal: (dealId: string, data: UpdateDealPayload) => Promise<void>
  deleteDeal: (dealId: string) => Promise<void>
  refreshDeals: () => Promise<void>
  linkDealToLand: (dealId: string, landId: string) => Promise<void>
  unlinkDealFromLand: (dealLandId: string) => Promise<void>
  fetchDealParticipants: (dealId: string) => Promise<DealParticipant[]>
  setLandData: (data: any) => void
}

const DealsContext = createContext<DealsContextState | undefined>(undefined)

interface DealsProviderProps {
  children: ReactNode
  landId?: string | null
  landData?: any | null
}

export const DealsProvider: React.FC<DealsProviderProps> = ({
  children,
  landId: initialLandId = null,
  landData: initialLandData = null,
}) => {
  const [landId, setLandId] = useState<string | null>(initialLandId)
  const [landData, setLandData] = useState<any | null>(initialLandData)
  const [deals, setDeals] = useState<DealLand[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Fetch all deals for a specific land
  const fetchDeals = useCallback(async (landIdParam: string) => {
    setLoading(true)
    setError(null)
    setLandId(landIdParam)

    try {
      // Fetch deals linked to this land
      const response = await apiClient.get(
        `/pipelines/pipelinekanbandealslands/land/${landIdParam}`
      )

      const dealLands: DealLand[] = response.data || []

      setDeals(dealLands)
    } catch (err: any) {
      console.error('Error fetching deals:', err)
      setError(err.response?.data?.message || 'Failed to fetch deals')
      setDeals([])
    } finally {
      setLoading(false)
    }
  }, [])

  // Create a new deal and link it to the land
  const addDeal = useCallback(
    async (dealData: CreateDealPayload) => {
      if (!landId) {
        setError('No land selected')
        return
      }

      setLoading(true)
      setError(null)

      try {
        // Step 1: Create the deal in the specified column
        const createDealResponse = await apiClient.post(
          `/pipelines/kanbandeals`,
          {
            title: dealData.title,
            informations: dealData.informations || {},
            pipeline_kanban_column_id: dealData.columnId,
          }
        )

        const newDeal = createDealResponse.data

        // Step 3: Link the deal to the land
        const linkResponse = await apiClient.post(
          '/pipelines/pipelinekanbandealslands',
          {
            pipeline_kanban_deal_id: newDeal.id,
            land_id: landId,
          }
        )

        const newDealLand: DealLand = {
          ...linkResponse.data,
          deal: newDeal,
        }

        // Update local state
        setDeals((prev) => [...prev, newDealLand])
      } catch (err: any) {
        console.error('Error creating deal:', err)
        setError(err.response?.data?.message || 'Failed to create deal')
        throw err
      } finally {
        setLoading(false)
      }
    },
    [landId]
  )

  // Update an existing deal
  const updateDeal = useCallback(
    async (dealId: string, data: UpdateDealPayload) => {
      setLoading(true)
      setError(null)

      try {
        const response = await apiClient.put(
          `/pipelines/kanbandeals/${dealId}`,
          data
        )

        const updatedDeal = response.data

        // Update local state - merge the updated deal data into the existing DealLand structure
        setDeals((prev) =>
          prev.map((dealLand) =>
            dealLand.id === dealId ? { ...dealLand, ...updatedDeal } : dealLand
          )
        )
      } catch (err: any) {
        console.error('Error updating deal:', err)
        setError(err.response?.data?.message || 'Failed to update deal')
        throw err
      } finally {
        setLoading(false)
      }
    },
    []
  )

  // Delete a deal (unlink from land and optionally delete the deal itself)
  const deleteDeal = useCallback(
    async (dealLandId: string) => {
      setLoading(true)
      setError(null)

      try {
        // Find the deal-land association to get dealId and landId
        const dealLand = deals.find((d) => d.id === dealLandId)
        if (!dealLand) {
          throw new Error('Deal-land association not found')
        }

        // Unlink from land using new endpoint with dealId and landId
        await apiClient.delete(
          `/pipelines/pipelinekanbandealslands/deal/${dealLand.pipelineKanbanDealId}/land/${dealLand.landId}`
        )

        // Update local state
        setDeals((prev) => prev.filter((d) => d.id !== dealLandId))
      } catch (err: any) {
        console.error('Error deleting deal:', err)
        setError(err.response?.data?.message || 'Failed to delete deal')
        throw err
      } finally {
        setLoading(false)
      }
    },
    [deals]
  )

  // Link an existing deal to the land
  const linkDealToLand = useCallback(
    async (dealId: string, landIdParam: string) => {
      setLoading(true)
      setError(null)

      try {
        const response = await apiClient.post(
          '/pipelines/pipelinekanbandealslands',
          {
            pipeline_kanban_deal_id: dealId,
            land_id: landIdParam,
          }
        )

        // Fetch the deal details
        const dealResponse = await apiClient.get(
          `/pipelines/kanbandeals/${dealId}`
        )

        const newDealLand: DealLand = {
          ...response.data,
          deal: dealResponse.data,
        }

        // Update local state
        setDeals((prev) => [...prev, newDealLand])
      } catch (err: any) {
        console.error('Error linking deal to land:', err)
        setError(err.response?.data?.message || 'Failed to link deal to land')
        throw err
      } finally {
        setLoading(false)
      }
    },
    []
  )

  // Unlink a deal from the land
  const unlinkDealFromLand = useCallback(
    async (dealLandId: string) => {
      setLoading(true)
      setError(null)

      try {
        // Find the deal-land association to get dealId and landId
        const dealLand = deals.find((d) => d.id === dealLandId)
        if (!dealLand) {
          throw new Error('Deal-land association not found')
        }

        // Unlink from land using new endpoint with dealId and landId
        await apiClient.delete(
          `/pipelines/pipelinekanbandealslands/deal/${dealLand.pipelineKanbanDealId}/land/${dealLand.landId}`
        )

        // Update local state
        setDeals((prev) => prev.filter((d) => d.id !== dealLandId))
      } catch (err: any) {
        console.error('Error unlinking deal from land:', err)
        setError(
          err.response?.data?.message || 'Failed to unlink deal from land'
        )
        throw err
      } finally {
        setLoading(false)
      }
    },
    [deals]
  )

  // Fetch deal participants
  const fetchDealParticipants = useCallback(
    async (dealId: string): Promise<DealParticipant[]> => {
      try {
        const response = await apiClient.get(
          `/pipelines/kanbandealparticipants/deal/${dealId}?includeUsers=true`
        )
        return response.data || []
      } catch (err: any) {
        console.error('Error fetching deal participants:', err)
        return []
      }
    },
    []
  )

  // Refresh deals
  const refreshDeals = useCallback(async () => {
    if (landId) {
      await fetchDeals(landId)
    }
  }, [landId, fetchDeals])

  const contextValue: DealsContextState = {
    landId,
    landData,
    deals,
    loading,
    error,
    fetchDeals,
    addDeal,
    updateDeal,
    deleteDeal,
    refreshDeals,
    linkDealToLand,
    unlinkDealFromLand,
    fetchDealParticipants,
    setLandData,
    total: deals.length,
  }

  return (
    <DealsContext.Provider value={contextValue}>
      {children}
    </DealsContext.Provider>
  )
}

// Custom hook to use the Deals context
export const useDeals = () => {
  const context = useContext(DealsContext)
  if (context === undefined) {
    throw new Error('useDeals must be used within a DealsProvider')
  }
  return context
}
